import { defineStore } from "pinia";
import DOMPurify from "dompurify";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { useAccountStore } from "@/_store/account.module";
import { useWorkspaceStore } from "@/_store/workspace.module";
import { StoreModule } from "@/constants/store.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import {
  SecurityGapAction,
  type SecurityGapType,
  SecurityGapWidgetType,
} from "@/constants/security-gaps.ts";
import { ToolApprovalStatus, ToolApprovalType } from "@/constants/ai-assistant.ts";
import type { TicketAction } from "@/constants/tickets.ts";

export enum MessageType {
  USER = "user",
  ASSISTANT = "assistant",
}

export enum ContentType {
  MARKDOWN = "markdown",
  EVENTS_SUMMARY = "eventsSummary",
  DOCUMENTATION_SEARCH = "documentationSearch",
  SECURITY_GAPS_SUMMARY = "securityGapsSummary",
  TOOL_APPROVAL_REQUIRED = "toolApprovalRequired",
}

export enum FeedbackRating {
  HELPFUL = "helpful",
  UNHELPFUL = "unhelpful",
}

export interface AiAssistantSession {
  sessionId: string;
  workspaceId: string;
  userId: string;
  messageCount: number;
  active: boolean;
  createdAt: string;
  expiresAt: string;
  lastActivityAt: string;
}

export interface CreateSessionRequest {
  workspaceId: string;
  userId: string;
  workspaceViewType: "msp" | "workspace";
}

export interface BaseAiContent {
  type: ContentType;
}

export interface MarkdownContent extends BaseAiContent {
  type: ContentType.MARKDOWN;
  markdown: string;
}

export interface EventsSummaryContent extends BaseAiContent {
  type: ContentType.EVENTS_SUMMARY;
  summary: string[];
  trends: string[];
  recommendations: EventsSummaryRecommendation[];
}

export interface EventsSummaryRecommendation {
  label: string;
  markdown: string;
  url: string;
}

export interface DocumentationSearchContent extends BaseAiContent {
  type: ContentType.DOCUMENTATION_SEARCH;
  markdown: string;
  sourceUrls: DocumentationSearchSourceUrl[];
}

export interface ToolApprovalRequiredContent extends BaseAiContent {
  type: ContentType.TOOL_APPROVAL_REQUIRED;
  approvalRequestId: string;
  toolCalls: ToolApprovalCall[];
}

export type ToolApprovalDoEventActionArguments = {
  eventId: string;
  action: TicketAction;
};

export type ToolApprovalSecurityGapArguments = {
  securityGapType: SecurityGapType;
};

export type SecurityGapToolApprovalType = Exclude<
  ToolApprovalType,
  ToolApprovalType.DO_EVENT_ACTION
>;

export type ToolApprovalCall = {
  toolExecutionRequestId: string;
  toolName: ToolApprovalType;
  toolArguments: string;
  status: ToolApprovalStatus;
};

export interface SecurityGapsOverviewContent extends BaseAiContent {
  type: ContentType.SECURITY_GAPS_SUMMARY;
  overview: string[];
  modules: Record<SecurityGapWidgetType, SecurityGapsAiModuleContent[]>;
}

export interface SecurityGapsAiModuleContent {
  misconfigurationType: SecurityGapType;
  // TODO: uncomment when we'll have API for this
  // affectedResourceCount: number;
  // affectedResourceType: "users" | "devices";
  affectedWorkspacesCount: number;
  content: string;
  actions: SecurityGapAction[];
}

export interface DocumentationSearchSourceUrl {
  url: string;
  label: string;
}

export type AiContentType =
  | MarkdownContent
  | EventsSummaryContent
  | DocumentationSearchContent
  | ToolApprovalRequiredContent
  | SecurityGapsOverviewContent;

export interface BaseMessage {
  type: MessageType;
}

export interface UserMessage extends BaseMessage {
  type: MessageType.USER;
  text: string;
}

export interface AssistantMessage extends BaseMessage {
  type: MessageType.ASSISTANT;
  content: AiContentType;
  suggestions: AiAssistantSuggestion[];
}

export type MessageUnion = UserMessage | AssistantMessage;

export interface SessionInfo {
  active: boolean;
  messageCount: number;
}

export interface AiAssistantMessage {
  id: string;
  sessionId: string;
  message: MessageUnion;
  sessionInfo: SessionInfo;
  feedbackRating?: FeedbackRating;
}

export interface AiAssistantSuggestion {
  text: string;
}

export interface SendMessageRequest {
  message: string;
}

export interface SubmitFeedbackRequest {
  rating: FeedbackRating;
}

export interface AiAssistantSuggestionsResponse {
  suggestions: AiAssistantSuggestion[];
}

export interface AiAssistantState {
  currentSession: AiAssistantSession | null;
  messages: AiAssistantMessage[];
  suggestions: AiAssistantSuggestion[];
  loading: boolean;
  messageHistory: AiAssistantMessage[];
  lastMessageTime: number;
  messageCount: number;
  rateLimitResetTime: number;
  refreshingSuggestions: boolean;
}

const defaultAiAssistantState: AiAssistantState = {
  currentSession: null,
  messages: [],
  suggestions: [],
  loading: false,
  refreshingSuggestions: false,
  messageHistory: [],
  lastMessageTime: 0,
  messageCount: 0,
  rateLimitResetTime: 0,
};

export const useAiAssistantStore = defineStore(StoreModule.AI_ASSISTANT, {
  state: (): AiAssistantState => ({ ...defaultAiAssistantState }),
  persist: {
    paths: ["currentSession"],
  },
  getters: {
    hasActiveSession: (state) =>
      state.currentSession !== null &&
      new Date(state.currentSession.expiresAt).getTime() > Date.now(),
    sessionExpired: (state) =>
      state.currentSession !== null &&
      new Date(state.currentSession.expiresAt).getTime() <= Date.now(),
    lastMessage: (state) => state.messages[state.messages.length - 1],
    canSendMessage: (state) => {
      const rateLimitCheck = checkRateLimit(state);
      return state.currentSession?.sessionId && !state.loading && rateLimitCheck.allowed;
    },
    rateLimitStatus: (state) => checkRateLimit(state),
    isLastMessageApprovalCard: (state) => {
      const lastMessage = state.messages.at(-1);
      return (
        lastMessage?.message.type === MessageType.ASSISTANT &&
        lastMessage.message.content.type === ContentType.TOOL_APPROVAL_REQUIRED
      );
    },
  },
  actions: {
    async createSession(): Promise<AiAssistantSession | null> {
      this.loading = true;

      const appsManagementModule = useAppsManagementModule();
      const workspaceStore = useWorkspaceStore();
      const accountStore = useAccountStore();

      const requestData: CreateSessionRequest = {
        workspaceId: workspaceStore.workspace.workspaceId,
        userId: accountStore.email, // Using email as user identifier
        workspaceViewType: appsManagementModule.isMspContext ? "msp" : "workspace",
      };

      if (!requestData.workspaceId || !requestData.userId) {
        console.error("Missing required workspaceId or userId for AI Assistant session");
        this.loading = false;
        return null;
      }

      const request = {
        ...api.aiAssistantCreateSession(),
        data: requestData,
      };
      try {
        const { data } = await axiosInstance.request<AiAssistantSession>(request);
        this.currentSession = data;
        this.messages = [];
        this.suggestions = [];
        return data;
      } catch (e) {
        console.error("Failed to create AI Assistant session:", e);
        return null;
      } finally {
        this.loading = false;
      }
    },

    async sendMessage(messageRequest: SendMessageRequest): Promise<AiAssistantMessage | null> {
      // This allows Learn More functionality to preserve chat history
      // Check rate limits before processing message
      // @TODO - Re-enable when we'll have UI for that
      // const rateLimitCheck = checkRateLimit(this);
      // if (!rateLimitCheck.allowed) {
      //   throw new Error(
      //     `Rate limit exceeded. Please wait ${Math.ceil((rateLimitCheck.waitTime || 0) / 1000)} seconds before sending another message.`
      //   );
      // }
      //
      // // Check message length limit
      // if (messageRequest.message.length > RATE_LIMIT_CONFIG.MAX_MESSAGE_LENGTH) {
      //   throw new Error(
      //     `Message is too long. Maximum ${RATE_LIMIT_CONFIG.MAX_MESSAGE_LENGTH.toLocaleString()} characters allowed.`
      //   );
      // }

      // Validate current session before sending a message
      if (!validateSession(this.currentSession)) {
        console.error("Invalid or expired session - creating new session");
        const session = await this.createSession();

        if (!session) {
          return null;
        }
      }

      // Sanitize user input to prevent XSS attacks
      const sanitizedMessage = sanitizeUserInput(messageRequest.message);
      const sanitizedRequest = { message: sanitizedMessage };

      // Add user message first so it's visible before loading starts
      const userMessageId = `msg-user-${Date.now()}`;
      const userMessage: AiAssistantMessage = {
        id: userMessageId,
        sessionId: this.currentSession!.sessionId,
        message: {
          type: MessageType.USER,
          text: sanitizedMessage,
        },
        sessionInfo: {
          messageCount: this.messages.length + 1,
          active: true,
        },
      };
      this.messages.push(userMessage);

      // Set loading to true AFTER adding user message so it's visible
      this.loading = true;

      const request = {
        ...api.aiAssistantSendMessage(this.currentSession!.sessionId),
        data: sanitizedRequest,
      };

      try {
        const { data } = await axiosInstance.request<AiAssistantMessage>(request);
        this.messages.push(data);

        // Update rate limiting counters
        this.lastMessageTime = Date.now();
        this.messageCount += 1;

        // Update suggestions if provided in response
        const assistantMessage = data.message as AssistantMessage;
        if (assistantMessage.suggestions?.length) {
          this.suggestions = assistantMessage.suggestions;
        }

        return data;
      } catch (e) {
        console.error("Failed to send message:", e);
        return null;
      } finally {
        this.loading = false;
      }
    },

    async getMessageHistory(): Promise<void> {
      if (!this.currentSession?.sessionId) {
        console.error("No active session to get message history");
        return;
      }

      this.loading = true;
      const request = {
        ...api.aiAssistantGetMessageHistory(this.currentSession.sessionId),
      };

      try {
        const { data } = await axiosInstance.request<AiAssistantMessage[]>(request);
        this.messageHistory = data;
        this.messages = data;
      } catch (e) {
        console.error("Failed to get message history:", e);
      } finally {
        this.loading = false;
      }
    },

    async refreshSuggestions(): Promise<AiAssistantSuggestion[]> {
      // Validate current session before refreshing suggestions
      if (!validateSession(this.currentSession)) {
        console.error("Invalid or expired session for suggestions refresh");
        return [];
      }

      this.refreshingSuggestions = true;
      const request = {
        ...api.aiAssistantRefreshSuggestions(this.currentSession!.sessionId),
        data: {},
      };

      try {
        const { data } = await axiosInstance.request<AiAssistantSuggestionsResponse>(request);
        this.suggestions = data.suggestions;
        return data.suggestions;
      } catch (e) {
        console.error("Failed to refresh suggestions:", e);
        return [];
      } finally {
        this.refreshingSuggestions = false;
      }
    },

    async handleAiToolApproval(
      action: "approve" | "reject",
      sessionId: string,
      approvalRequestId: string,
      toolExecutionRequestId: string
    ): Promise<void> {
      if (!sessionId || !approvalRequestId || !toolExecutionRequestId) {
        console.error(`Missing required data to ${action} AI tool execution`);
        return;
      }

      const apiMethod =
        action === "approve" ? api.approveAiToolExecution : api.rejectAiToolExecution;

      try {
        const request = apiMethod(sessionId, approvalRequestId, toolExecutionRequestId);
        const { data } = await axiosInstance.request<AiAssistantMessage>(request);

        const responseId = getApprovalRequestIdFromMessage(data);

        // SCENARIO 1: The response is just the updated card (still the same ID)
        if (responseId === approvalRequestId) {
          upsertAssistantMessage(this.$state, data);
        }
        // SCENARIO 2: The response is NEW content (Tool finished, ID is null or different)
        else {
          // Step 1: Refresh the OLD card first so it shows "Approved/Rejected"
          await this.refreshToolApprovalMessage(sessionId, approvalRequestId);

          // Step 2: Append the NEW content (the tool result)
          upsertAssistantMessage(this.$state, data);
        }
      } catch (e) {
        console.error(`Failed to ${action} AI tool execution:`, e);
      }
    },

    async refreshToolApprovalMessage(sessionId: string, approvalRequestId: string): Promise<void> {
      if (!sessionId || !approvalRequestId) {
        console.error("Missing required data to refresh AI tool approval message");
        return;
      }

      const approvalMessageIndex = findApprovalMessageIndex(this.messages, approvalRequestId);

      // Don't fetch if the message isn't in our UI
      if (approvalMessageIndex === -1) {
        return;
      }

      try {
        const request = api.getToolApprovalMessage(sessionId, approvalRequestId);
        const { data } = await axiosInstance.request<AiAssistantMessage>(request);

        if (getApprovalRequestIdFromMessage(data) !== approvalRequestId) {
          console.error("Unexpected response when refreshing tool approval message");
          return;
        }

        upsertAssistantMessage(this.$state, data);
      } catch (e) {
        console.error("Failed to refresh AI tool approval message:", e);
      }
    },

    async submitFeedback(messageId: string, feedback: SubmitFeedbackRequest): Promise<boolean> {
      if (!this.currentSession?.sessionId) {
        console.error("No active session for feedback submission");
        return false;
      }

      if (!messageId || messageId === "undefined") {
        console.error("Invalid messageId for feedback submission:", messageId);
        return false;
      }

      this.loading = true;
      const request = {
        ...api.aiAssistantSubmitFeedback(this.currentSession.sessionId, messageId),
        data: feedback,
      };

      try {
        await axiosInstance.request(request);

        // Update the feedback in the local message
        const message = this.messages.find((msg) => msg.id === messageId);
        if (message) {
          message.feedbackRating = feedback.rating;
        }

        return true;
      } catch (e) {
        console.error("Failed to submit feedback:", e);
        return false;
      } finally {
        this.loading = false;
      }
    },

    async ensureActiveSession(): Promise<boolean> {
      if (this.hasActiveSession) {
        return true;
      }

      if (this.sessionExpired) {
        this.$reset();
      }

      const session = await this.createSession();
      return session !== null;
    },
  },
});

// Helper function to sanitize user input
const sanitizeUserInput = (input: string): string => {
  // Configure DOMPurify to strip all HTML tags and return plain text only
  const config = {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  };
  return DOMPurify.sanitize(input.trim(), config);
};

// Helper function to validate session
const validateSession = (session: AiAssistantSession | null): boolean => {
  if (!session) {
    return false;
  }

  // Check if session has expired
  if (new Date(session.expiresAt).getTime() <= Date.now()) {
    return false;
  }

  // Basic session ID validation
  if (!session.sessionId || session.sessionId.length < 10) {
    return false;
  }

  return true;
};

const isAssistantMessage = (message: MessageUnion): message is AssistantMessage =>
  message.type === MessageType.ASSISTANT;

const isToolApprovalRequiredContent = (
  content: AiContentType
): content is ToolApprovalRequiredContent => content.type === ContentType.TOOL_APPROVAL_REQUIRED;

// Helper to safely extract ID
const getApprovalRequestIdFromMessage = (message?: AiAssistantMessage): string | null => {
  if (!message || !isAssistantMessage(message.message)) return null;
  const { content } = message.message;
  return isToolApprovalRequiredContent(content) ? content.approvalRequestId : null;
};

const findApprovalMessageIndex = (
  messages: AiAssistantMessage[],
  approvalRequestId: string
): number => {
  // Search backwards (optimization: approval cards are likely recent)
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    const id = getApprovalRequestIdFromMessage(msg);
    if (id === approvalRequestId) return i;
  }
  return -1;
};

const upsertAssistantMessage = (
  state: AiAssistantState,
  incomingMessage: AiAssistantMessage
): void => {
  if (!isAssistantMessage(incomingMessage.message)) {
    console.error("Unexpected response message type:", incomingMessage.message.type);
    return;
  }

  const incomingId = getApprovalRequestIdFromMessage(incomingMessage);

  // 1. If the incoming message has an ID, try to find it in our list
  let existingIndex = -1;
  if (incomingId) {
    existingIndex = findApprovalMessageIndex(state.messages, incomingId);
  }

  if (existingIndex !== -1) {
    // We found the card, replace it (e.g. changing status "Pending" -> "Approved")
    state.messages.splice(existingIndex, 1, incomingMessage);
  } else {
    // It's a new message (or a result of the tool), append it
    state.messages.push(incomingMessage);
  }

  // Update suggestions if present
  if (incomingMessage.message.suggestions?.length) {
    state.suggestions = incomingMessage.message.suggestions;
  }
};

// Rate limiting configuration
const RATE_LIMIT_CONFIG = {
  MAX_MESSAGES_PER_MINUTE: 10,
  MIN_TIME_BETWEEN_MESSAGES: 2000, // 2 seconds
  RATE_LIMIT_WINDOW: 60000, // 1 minute
  MAX_MESSAGE_LENGTH: 10000, // 10,000 characters
};

// Export character limit for use in components
export const MAX_MESSAGE_LENGTH = RATE_LIMIT_CONFIG.MAX_MESSAGE_LENGTH;

// Helper function to check rate limits
const checkRateLimit = (state: AiAssistantState): { allowed: boolean; waitTime?: number } => {
  const now = Date.now();

  // Reset counter if window has passed
  if (now > state.rateLimitResetTime) {
    state.messageCount = 0;
    state.rateLimitResetTime = now + RATE_LIMIT_CONFIG.RATE_LIMIT_WINDOW;
  }

  // Check minimum time between messages
  const timeSinceLastMessage = now - state.lastMessageTime;
  if (timeSinceLastMessage < RATE_LIMIT_CONFIG.MIN_TIME_BETWEEN_MESSAGES) {
    const waitTime = RATE_LIMIT_CONFIG.MIN_TIME_BETWEEN_MESSAGES - timeSinceLastMessage;
    console.error(`Rate limit: Must wait ${waitTime}ms between messages`);
    return { allowed: false, waitTime };
  }

  // Check messages per minute limit
  if (state.messageCount >= RATE_LIMIT_CONFIG.MAX_MESSAGES_PER_MINUTE) {
    const waitTime = state.rateLimitResetTime - now;
    console.error(`Rate limit: Exceeded maximum messages per minute. Reset in ${waitTime}ms`);
    return { allowed: false, waitTime };
  }

  return { allowed: true };
};
